import { rydeColors } from "@ryde/theme";

import type { TLiveMapIncidentMarkerIconType } from "./live-map.incident-marker.models";

/**
 * Negative ids reserved for live map debug overlay markers (no collision with real incidents).
 */
export const LIVE_MAP_DEBUG_FAKE_INCIDENT_ID_BASE = -1_000_000_000;

export const liveMapDebugFakeIncidentIdForRide = (rideId: number): number =>
  LIVE_MAP_DEBUG_FAKE_INCIDENT_ID_BASE - rideId;

/**
 * Half-range in degrees for pseudo-random offset from the ride’s first station (~±170 m at the equator).
 */
export const LIVE_MAP_DEBUG_FAKE_OFFSET_HALF_RANGE_DEG = 0.0015;

/** In `?debugMode`, add a fake incident marker only on every Nth ride (keeps map readable). */
export const LIVE_MAP_DEBUG_INCIDENT_RIDE_STRIDE = 2;

export type ILiveMapDebugIncidentProfile = {
  incidentTypeId?: number;
  incidentTypeIconType?: TLiveMapIncidentMarkerIconType;
  incidentTypeColor?: string;
  typeLabel: string;
};

/** Alternate badge colors in `?debugMode` (every second typed marker). */
export const LIVE_MAP_DEBUG_INCIDENT_ALTERNATE_COLORS: Partial<
  Record<TLiveMapIncidentMarkerIconType, string>
> = {
  security: rydeColors.liveMapDebugIncidentPurple,
  repair: rydeColors.liveMapDebugIncidentAlternateRepair,
  ride_event: rydeColors.liveMapDebugIncidentAlternateRideEvent,
  road_block: rydeColors.liveMapDebugIncidentAlternateRoadBlock,
};

/** `?debugMode`: cycles 4 enum icons + 1 default orange dot (one fake marker per ride, in list order). */
export const LIVE_MAP_DEBUG_INCIDENT_PROFILES: ILiveMapDebugIncidentProfile[] =
  [
    {
      incidentTypeId: 4,
      incidentTypeIconType: "security",
      incidentTypeColor: rydeColors.liveMapIncidentSecurity,
      typeLabel: "Security",
    },
    {
      incidentTypeId: 2,
      incidentTypeIconType: "repair",
      incidentTypeColor: rydeColors.liveMapDebugIncidentRepair,
      typeLabel: "Repair",
    },
    {
      incidentTypeId: 3,
      incidentTypeIconType: "ride_event",
      incidentTypeColor: rydeColors.liveMapDebugIncidentRideEvent,
      typeLabel: "Ride event",
    },
    {
      incidentTypeId: 1,
      incidentTypeIconType: "road_block",
      incidentTypeColor: rydeColors.liveMapDebugIncidentRoadBlock,
      typeLabel: "Road block",
    },
    {
      typeLabel: "Default (no icon)",
    },
  ];

/** Tie-break among incident markers within the incidents pane. */
export const LIVE_MAP_INCIDENT_MARKER_Z_INDEX_OFFSET = 500;

/** `className` on `Popup` — used in global styles for shadow, padding, and the Leaflet close control. */
export const LIVE_MAP_INCIDENT_LEAFLET_POPUP_CLASS =
  "ryde-live-map-incident-leaflet-popup";

/** BEM class names for incident popup (styles scoped under `IncidentPopupRoot`). */
export const incidentPopupCn = {
  title: "ryde-live-map-incident-popup__title",
  typeRow: "ryde-live-map-incident-popup__type-row",
  typeTrigger: "ryde-live-map-incident-popup__type-trigger",
  typeName: "ryde-live-map-incident-popup__type-name",
  valueDriverHint: "ryde-live-map-incident-popup__value--driver-hint",
  creator: "ryde-live-map-incident-popup__creator",
  body: "ryde-live-map-incident-popup__body",
  row: "ryde-live-map-incident-popup__row",
  label: "ryde-live-map-incident-popup__label",
  value: "ryde-live-map-incident-popup__value",
  footer: "ryde-live-map-incident-popup__footer",
  close: "ryde-live-map-incident-popup__close",
  open: "ryde-live-map-incident-popup__open",
} as const;
